import { Suspense, lazy, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Provider } from 'react-redux';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import 'normalize.css/normalize.css';
import { ThemeProvider } from 'styled-components';

import { ROUTE_PERMISSIONS } from 'constants/routePermissions';

import { useAppSelector } from 'redux/hooks';
import { store } from 'redux/store';

import { AuthIsNotSignedIn, AuthIsSignedIn, AuthProvider, BucketProvider } from 'contexts';
import { ChatProvider } from 'contexts/ChatContext';

import { PRICE_SUPPRESSED_ZONES } from 'hooks/usePriceVisible';

import { GlobalStyle } from 'themes/globalStyles';
import { theme } from 'themes/theme';

import { routes } from 'data/routes';

import { BudgetsPage } from 'pages/Budgets';
import { CataloguePage } from 'pages/Catalogue';
// Customer Mapper feature disabled for now
// import { CustomerMapperPage } from 'pages/CustomerMapper';
import { DownloadsPage } from 'pages/Downloads';
import { HomePage } from 'pages/Home';
import { InfoPage } from 'pages/Info';
import { LoginPage } from 'pages/Login';
import { NotificationsPage } from 'pages/Notifications';
import { OrdersPage } from 'pages/Orders';
import { PricesPage } from 'pages/Prices';
import { SelectionsPage } from 'pages/Selections';

import { ProtectedRoute } from 'components/ProtectedRoute';
import { SupportTicketWidget } from 'components/SupportTicketWidget';

// Lazy: keeps recharts (+ its d3 tree) out of the main bundle for users who
// never open /statistics. App already renders inside <Suspense>.
const StatisticsPage = lazy(() =>
  import('pages/Statistics').then((m) => ({ default: m.StatisticsPage }))
);

export function App() {
  const getPermissions = (path: string): string[] =>
    ROUTE_PERMISSIONS.find((r) => r.path === path)?.permissions ?? [];

  // Customer Mapper feature disabled for now — only consumer of this helper
  // const getExcludePermissions = (path: string): string[] =>
  //   ROUTE_PERMISSIONS.find((r) => r.path === path)?.excludePermissions ?? [];

  function MainRoute(): React.ReactElement {
    const zone = useAppSelector((state) => state.settings.zone);
    const isPriceZoneBlocked = PRICE_SUPPRESSED_ZONES.includes(zone);

    return (
      <ChatProvider>
        <Routes>
          <Route path="/" element={<Navigate to={routes.home} />} />

          <Route path={routes.home} element={<HomePage />} />

          <Route
            path={`${routes.catalogue}/*`}
            element={
              <ProtectedRoute permissions={getPermissions(routes.catalogue)}>
                <CataloguePage />
              </ProtectedRoute>
            }
          />

          <Route path={`${routes.info}/*`} element={<InfoPage />} />

          <Route path={`${routes.downloads}/*`} element={<DownloadsPage />} />

          <Route path={`${routes.selections}/*`} element={<SelectionsPage />} />

          <Route
            path={`${routes.orders}/*`}
            element={
              <ProtectedRoute permissions={getPermissions(routes.orders)}>
                <OrdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path={`${routes.statistics}/*`}
            element={
              <ProtectedRoute permissions={getPermissions(routes.statistics)}>
                <StatisticsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path={`${routes.budgets}/*`}
            element={
              <ProtectedRoute permissions={getPermissions(routes.budgets)}>
                <BudgetsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path={`${routes.notifications}/*`}
            element={
              <ProtectedRoute permissions={getPermissions(routes.notifications)}>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* Customer Mapper feature disabled for now */}
          {/* <Route
            path={`${routes.customerMapper}/*`}
            element={
              <ProtectedRoute
                permissions={getPermissions(routes.customerMapper)}
                excludePermissions={getExcludePermissions(routes.customerMapper)}
              >
                <CustomerMapperPage />
              </ProtectedRoute>
            }
          /> */}

          <Route
            path={`${routes.prices}/*`}
            element={
              isPriceZoneBlocked ? (
                <Navigate to={routes.home} replace />
              ) : (
                <ProtectedRoute permissions={getPermissions(routes.prices)}>
                  <PricesPage />
                </ProtectedRoute>
              )
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ChatProvider>
    );
  }

  function SignInRoute(): React.ReactElement {
    return (
      <Routes>
        <Route path="/" element={<Navigate to={routes.login} />} />

        <Route path={`${routes.login}/*`} element={<LoginPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  useEffect(() => {
    window.process = {
      ...window.process,
    };
  }, []);

  return (
    <Suspense fallback="Loading ...">
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                padding: '1.5rem',
                borderRadius: '0',
                color: '#ffffff',
                textTransform: 'uppercase',
                fontFamily: 'LFT Zeno, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
              },
              success: {
                style: {
                  background: 'rgba(92, 184, 92, 0.9)',
                },
                iconTheme: {
                  primary: '#ffffff',
                  secondary: 'rgba(92, 184, 92, 0.9)',
                },
              },
              error: {
                style: {
                  background: 'rgba(255, 0, 0, 0.85)',
                },
                iconTheme: {
                  primary: '#ffffff',
                  secondary: 'rgba(255, 0, 0, 0.85)',
                },
              },
              loading: {
                style: {
                  background: 'rgba(85, 85, 85, 0.9)',
                },
              },
            }}
          />
          <GlobalStyle />

          <Router>
            <AuthProvider>
              <AuthIsNotSignedIn>
                <SignInRoute />
              </AuthIsNotSignedIn>

              <AuthIsSignedIn>
                <BucketProvider>
                  <MainRoute />
                </BucketProvider>

                <SupportTicketWidget />
              </AuthIsSignedIn>
            </AuthProvider>
          </Router>

          <div id="portalContext" />
        </ThemeProvider>
      </Provider>
    </Suspense>
  );
}
